import React, { useState, useEffect } from "react";
import { Zap, CalendarClock, Send, FileText, Check, X, Loader2, Rocket, AlertTriangle } from "lucide-react";
import { getAutomationSettings, updateAutomationSettings, runAutoPoster, runDigest, getAutomationQueue, decideQueueItem } from "../../api";
import { useToast } from "../../hooks/use-toast";
import { Switch } from "../ui/switch";

const GrowthAutomation = () => {
  const { toast } = useToast();
  const [settings, setSettings] = useState({ autoPosterEnabled: false, digestEnabled: false, autoPublishEnabled: false });
  const [queue, setQueue] = useState([]);
  const [busy, setBusy] = useState(null);

  const refresh = () => {
    getAutomationSettings().then(setSettings).catch(() => {});
    getAutomationQueue().then(setQueue).catch(() => {});
  };
  useEffect(() => { refresh(); }, []);

  const toggle = async (key, val) => {
    setSettings((s) => ({ ...s, [key]: val }));
    try { await updateAutomationSettings({ [key]: val }); toast({ title: `${key === "autoPosterEnabled" ? "Auto-poster" : "Weekly digest"} ${val ? "enabled" : "disabled"}` }); }
    catch { toast({ title: "Update failed" }); refresh(); }
  };
  const trigger = async (fn, label, key) => {
    setBusy(key);
    try { await fn(); toast({ title: `${label} queued for approval` }); refresh(); }
    catch { toast({ title: "Run failed" }); } finally { setBusy(null); }
  };
  const decide = async (id, decision) => {
    try { await decideQueueItem(id, decision); toast({ title: decision === "approve" ? "Approved & scheduled" : "Rejected" }); refresh(); }
    catch { toast({ title: "Action failed" }); }
  };

  const pending = queue.filter((q) => q.status === "pending-approval");

  return (
    <div className="space-y-6" data-testid="growth-automation">
      <div className="grid sm:grid-cols-2 gap-5">
        <div className="bg-white rounded-2xl border border-[#f0ece5] p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="font-display text-[18px] font-500 text-[#2b2b2b] flex items-center gap-2"><Send size={17} className="text-[#b8935f]" /> Social Auto-Poster</p>
            <Switch checked={settings.autoPosterEnabled} onCheckedChange={(v) => toggle("autoPosterEnabled", v)} data-testid="autoposter-switch" />
          </div>
          <p className="text-[13px] text-[#8a8a8a] mb-4">Twice daily (09:00 & 18:00) queues a listing post for your approval. Approved posts can auto-publish live via the toggle below.</p>
          <button onClick={() => trigger(runAutoPoster, "Post", "poster")} disabled={busy === "poster"} data-testid="run-poster-btn" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-[#e2d8c8] text-[13px] text-[#6a6a6a] hover:border-[#b8935f] hover:text-[#b8935f] transition-colors disabled:opacity-60">{busy === "poster" ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} />} Run now</button>
          {settings.lastPosterRun && <p className="text-[11px] text-[#a0a0a0] mt-3">Last run: {new Date(settings.lastPosterRun).toLocaleString("en-GB")}</p>}
        </div>
        <div className="bg-white rounded-2xl border border-[#f0ece5] p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="font-display text-[18px] font-500 text-[#2b2b2b] flex items-center gap-2"><FileText size={17} className="text-[#b8935f]" /> Weekly Market Digest</p>
            <Switch checked={settings.digestEnabled} onCheckedChange={(v) => toggle("digestEnabled", v)} data-testid="digest-switch" />
          </div>
          <p className="text-[13px] text-[#8a8a8a] mb-4">Every Monday 08:00 compiles a market digest for your approval before sending.</p>
          <button onClick={() => trigger(runDigest, "Digest", "digest")} disabled={busy === "digest"} data-testid="run-digest-btn" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-[#e2d8c8] text-[13px] text-[#6a6a6a] hover:border-[#b8935f] hover:text-[#b8935f] transition-colors disabled:opacity-60">{busy === "digest" ? <Loader2 size={14} className="animate-spin" /> : <CalendarClock size={14} />} Run now</button>
          {settings.lastDigestRun && <p className="text-[11px] text-[#a0a0a0] mt-3">Last run: {new Date(settings.lastDigestRun).toLocaleString("en-GB")}</p>}
        </div>
      </div>

      <div className={`rounded-2xl border p-6 ${settings.autoPublishEnabled ? "border-[#b8935f] bg-[#b8935f]/5" : "border-[#f0ece5] bg-white"}`} data-testid="autopublish-card">
        <div className="flex items-center justify-between mb-2">
          <p className="font-display text-[18px] font-500 text-[#2b2b2b] flex items-center gap-2"><Rocket size={17} className="text-[#b8935f]" /> Auto-Publish Scheduled Posts <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-100 text-green-700 uppercase tracking-wide">Live</span></p>
          <Switch checked={settings.autoPublishEnabled} onCheckedChange={(v) => toggle("autoPublishEnabled", v)} data-testid="autopublish-switch" />
        </div>
        <p className="text-[13px] text-[#8a8a8a] mb-3">When on, any post you queue with a date/time in the Scheduler will publish <span className="font-500 text-[#6a6a6a]">live to your Facebook Page / Instagram</span> automatically once its time arrives (checked every 10 minutes).</p>
        <p className="text-[12px] text-amber-700 flex items-center gap-1.5"><AlertTriangle size={13} /> This posts publicly without further approval. Leave off if you'd rather approve each post by hand.</p>
      </div>

      <div className="bg-white rounded-2xl border border-[#f0ece5] p-6">
        <h3 className="font-display text-[18px] font-500 text-[#2b2b2b] mb-4">Approval Queue {pending.length > 0 && <span className="text-[13px] text-[#b8935f]">({pending.length} pending)</span>}</h3>
        {queue.length === 0 ? <p className="p-6 text-center text-[#8a8a8a]">Nothing queued yet. Run the auto-poster or digest above.</p> : (
          <div className="space-y-3">
            {queue.map((q) => (
              <div key={q.id} className="p-4 rounded-xl bg-[#faf7f2] border border-[#f0ece5]" data-testid="queue-item">
                <div className="flex items-start gap-3">
                  {q.image && <img src={q.image} alt="" className="w-16 h-16 rounded-lg object-cover" />}
                  <div className="flex-1">
                    <p className="text-[11px] uppercase tracking-wide text-[#b8935f]">{q.kind === "market-digest" ? "Market Digest" : `${q.platform} post`}</p>
                    <p className="text-[13px] text-[#4a4a4a] mt-1 whitespace-pre-line line-clamp-3">{q.caption || q.body}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {q.status === "pending-approval" ? (
                      <>
                        <button onClick={() => decide(q.id, "approve")} data-testid="approve-queue-btn" className="w-9 h-9 rounded-lg border border-[#e2d8c8] flex items-center justify-center text-green-600 hover:border-green-400 transition-colors"><Check size={16} /></button>
                        <button onClick={() => decide(q.id, "reject")} className="w-9 h-9 rounded-lg border border-[#e2d8c8] flex items-center justify-center text-red-500 hover:border-red-400 transition-colors"><X size={16} /></button>
                      </>
                    ) : (
                      <span className={`text-[11px] px-2.5 py-1 rounded-full capitalize ${q.status === "approved" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>{q.status}</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default GrowthAutomation;
